
import Vue from 'vue';
import { CourseModel } from '@descomplica/dex-models';
import { Loading, Icon } from '@descomplica/vue-components';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { ListModel as StudyPlanningListModel } from '@/models/studyPlanning/ListModel';
import { showPostgradErrorToast } from '@/helpers/postgradErrorToast';
import {
  ConfigModal,
  Header,
  WeeklyLists,
  OverdueUserWarningModal,
} from '@/components/studyPlanning';
import PostGradAssessmentsCard from '@/components/cards/assessments/PostGradAssessmentsCard.vue';
import GraduationDateCard from '@/components/cards/postgrad/GraduationDateCard.vue';
import DefaultStudyPlanningModal from '@/components/studyPlanning/postgrad/DefaultStudyPlanningModal.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { StudyPlanningBreakpointModel } from '@/models/studyPlanning/StudyPlanningBreakpointModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { ContractExtensionEligibilityInfo } from '@/models/contractExtension/ContractExtensionEligibilityModel';
import InvalidStudyPlanningCard from '@/components/studyPlanning/postgrad/InvalidStudyPlanningCard.vue';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

interface Data {
  showStudyPlanningConfigModal: boolean;
  showDefaultStudyPlanningModal: boolean;
  isFetchingStudyPlanning: boolean;
  isFetchingBreakpointsForDefaultStudyPlanning: boolean;
  studyPlanningFetchError: boolean;
  defaultStudyPlanningCreationError: boolean;
  isStudyPlanningExpired: boolean;
  extensionRequestloading: boolean;
  retryCreatingDefaultStudyPlanning: boolean;
}

export default Vue.extend({
  components: {
    ConfigModal,
    Header,
    WeeklyLists,
    Loading,
    PostGradAssessmentsCard,
    OverdueUserWarningModal,
    GraduationDateCard,
    DefaultStudyPlanningModal,
    InvalidStudyPlanningCard,
    Icon,
  },
  data(): Data {
    return {
      showStudyPlanningConfigModal: false,
      showDefaultStudyPlanningModal: false,
      isFetchingStudyPlanning: false,
      isFetchingBreakpointsForDefaultStudyPlanning: false,
      studyPlanningFetchError: false,
      defaultStudyPlanningCreationError: false,
      isStudyPlanningExpired: false,
      extensionRequestloading: false,
      retryCreatingDefaultStudyPlanning: false,
    };
  },
  computed: {
    isLoadingUserBaseProgress(): boolean {
      return this.$store.getters.isLoadingUserBaseProgress;
    },
    isLoadingStudyPlanning(): boolean {
      return this.isFetchingStudyPlanning || this.isFetchingBreakpointsForDefaultStudyPlanning;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    canViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    isUserInTrialCourse(): boolean {
      return !!this.$store.getters.trialByDescomplicaCourseId(this.currentChimeraCourse?._id);
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    possibleStudyPlanningBreakpoints(): StudyPlanningBreakpointModel[] {
      return this.$store.getters.studyPlanningBreakpoints.filter(
        (b: StudyPlanningBreakpointModel) => b.isPossible,
      );
    },
    showPostgradAssessmentsCard(): boolean {
      return this.$store.getters.showPostgradAssessmentsCard;
    },
    hasAssessmentsInPostGradAssessmentsCard(): boolean {
      return this.$store.getters.assessments.length === 3;
    },
    courseEligibleForExtensionInformation(): ContractExtensionEligibilityInfo | null {
      const eligibilityInformation =
        this.$store.getters.contractExtensionEligibilityInformationByCourseId(
          this.currentCourse.id,
        );
      return eligibilityInformation;
    },
    courseEligibleForExtension(): boolean {
      return (
        !!this.courseEligibleForExtensionInformation?.extensionOptions.length &&
        !this.courseEligibleForExtensionInformation.isExpired
      );
    },
  },
  watch: {
    canViewPostgradStudyPlanning: {
      handler(value: boolean) {
        if (!value) {
          this.redirectToRoute('home');
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.isFetchingStudyPlanning = true;

    if (!this.courseEligibleForExtensionInformation) {
      this.$store.dispatch('GET_POSTGRAD_CONTRACT_EXTENSION_ELIGIBILITY', {
        course: this.currentCourse,
        chimeraCourse: this.currentChimeraCourse,
      });
    }

    try {
      await this.$store.dispatch('GET_STUDY_PLANNING', this.currentCourse.id);
    } catch (err) {
      showPostgradErrorToast(err as Error, this.$t('getStudyPlanningError').toString());
      this.studyPlanningFetchError = true;

      Analytics.trackEvent(EVENT_LABELS.retrievePostgradStudyPlanningError, {
        course: this.currentCourse.name,
      });

      return;
    } finally {
      this.isFetchingStudyPlanning = false;
    }

    if (!this.studyPlanning) {
      await this.handleUserWithoutStudyPlanning();

      return;
    }

    if (!this.studyPlanning.currentWeek) {
      this.isStudyPlanningExpired = true;
    }
  },
  methods: {
    setDefaultStudyPlanningCreationError(): void {
      this.defaultStudyPlanningCreationError = true;
    },
    redirectToRoute(routeName: 'home' | 'classrooms'): void {
      this.$router.push({
        name: routeName,
        params: {
          category: this.currentCourse.descomplicaCategorySlug as string,
          currentCourse: this.currentCourse.slug as string,
        },
      });
    },
    toggleStudyPlanningConfigModal(
      location?: 'header' | 'invalid_card' | 'onboarding' | 'warning',
    ): void {
      this.showStudyPlanningConfigModal = !this.showStudyPlanningConfigModal;

      if (this.showStudyPlanningConfigModal) {
        Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
          in_postgrad_study_planning_edit_button: true,
          button_location: location,
          course: this.currentCourse.name,
          category: 'pos',
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      } else {
        Analytics.trackEvent(EVENT_LABELS.modalClosed, {
          in_postgrad_study_planning_config_modal: true,
          course: this.currentCourse.name,
          category: 'pos',
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      }
    },
    async handleUserWithoutStudyPlanning(): Promise<void> {
      if (!this.isPostgraduateCourse) return;

      this.isFetchingBreakpointsForDefaultStudyPlanning = true;

      try {
        await this.$store.dispatch('GET_STUDY_PLANNING_BREAKPOINTS', this.currentCourse.id);

        if (!this.possibleStudyPlanningBreakpoints.length) {
          this.setDefaultStudyPlanningCreationError();
          return;
        }

        this.showDefaultStudyPlanningModal = true;

        Analytics.trackEvent(EVENT_LABELS.modalViewed, {
          in_default_postgrad_studyplanning: true,
          course: this.currentCourse.name,
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      } catch (err) {
        Analytics.trackEvent(EVENT_LABELS.postgradStudyPlanningBreakpointsError, {
          in_default_postgrad_studyplanning: true,
          course: this.currentCourse.name,
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      } finally {
        this.isFetchingBreakpointsForDefaultStudyPlanning = false;
      }
    },
    closeDefaultStudyPlanningModal(): void {
      this.showDefaultStudyPlanningModal = false;
      this.retryCreatingDefaultStudyPlanning = false;
    },
    async refreshLists(callback?: Function): Promise<void> {
      const { studyPlanningWeekLists }: { studyPlanningWeekLists: StudyPlanningListModel[] } =
        this.$store.getters;

      if (!this.studyPlanning || !studyPlanningWeekLists) {
        if (callback) callback();
        return;
      }

      const { studyPlanningAnticipatedWeeks } = this.$store.getters;
      const studyPlanningAnticipatedLists = studyPlanningAnticipatedWeeks(this.studyPlanning.id)
        .map((anticipatedWeek: any) => anticipatedWeek.anticipatedLists)
        .flat();
      const classroomsFromStudyPlanningLists: number[] = [
        ...studyPlanningWeekLists,
        ...studyPlanningAnticipatedLists,
      ].map((l: StudyPlanningListModel) => l.classroomId);
      const uniqueClassroomIds = [...new Set(classroomsFromStudyPlanningLists)];

      await Promise.all(uniqueClassroomIds.map(this.pullMaterialsFromClassroom));

      if (callback) callback();
    },
    async pullMaterialsFromClassroom(id: number) {
      await this.$store.dispatch('PULL_CLASSROOM_INFO_FOR_POSTGRAD_STUDYPLANNING', {
        classroomId: id,
      });
    },
    scrollToCard(): void {
      const elementToScroll = this.$refs.expirationCard;
      // @ts-ignore
      if (elementToScroll) elementToScroll.$el.scrollIntoView({ behavior: 'smooth' });
    },
    retryCreateDefaultStudyPlanning(): void {
      this.showDefaultStudyPlanningModal = true;
      this.retryCreatingDefaultStudyPlanning = true;
    },
  },
});
